/**
 * Transport errors.
 *
 * NOTE — deliberate difference from WalletFront: nothing here builds a
 * user-facing sentence. WalletFront's `client.js` composed Russian strings
 * ("Сессия истекла. Войдите заново.") inside the transport layer, which made the
 * app untranslatable and put copy decisions in the network code. Here the
 * transport carries only machine-readable facts — status, `errorCode`,
 * `retryAfterSeconds` — and the UI turns them into words through i18n.
 */

export type ApiErrorKind = 'http' | 'network' | 'timeout' | 'rate-limit' | 'unauthorized' | 'parse';

export interface ApiErrorInit {
  kind: ApiErrorKind;
  status?: number;
  /** Backend `errorCode` from the envelope — the i18n lookup key. */
  errorCode?: string | null;
  /** Backend `error` text. Diagnostics only; never render it raw. */
  detail?: string | null;
  method?: string;
  path?: string;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly errorCode: string | null;
  readonly detail: string | null;
  readonly method: string | undefined;
  readonly path: string | undefined;

  constructor(init: ApiErrorInit) {
    // The message is for logs and stack traces, not for the screen.
    super(
      `${init.kind}${init.status ? ` ${init.status}` : ''} ${init.method ?? ''} ${init.path ?? ''}`.trim()
    );
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.errorCode = init.errorCode ?? null;
    this.detail = init.detail ?? null;
    this.method = init.method;
    this.path = init.path;
    if (init.cause !== undefined) this.cause = init.cause;
  }

  /** i18n key the UI should look up, falling back to a generic message. */
  get translationKey(): string {
    if (this.errorCode) return `errors.code.${this.errorCode}`;
    return `errors.kind.${this.kind}`;
  }
}

export class RateLimitError extends ApiError {
  readonly retryAfterSeconds: number;
  readonly requestKey: string;

  constructor(
    init: Omit<ApiErrorInit, 'kind'> & { retryAfterSeconds: number; requestKey: string }
  ) {
    super({ ...init, kind: 'rate-limit', status: init.status ?? 429 });
    this.name = 'RateLimitError';
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.requestKey = init.requestKey;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(init: Omit<ApiErrorInit, 'kind'> = {}) {
    super({ ...init, kind: 'unauthorized', status: init.status ?? 401 });
    this.name = 'UnauthorizedError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}

export function isUnauthorizedError(error: unknown): error is UnauthorizedError {
  return error instanceof UnauthorizedError;
}
